// custom (Metas · F3): helpers PUROS de formatação e período para as telas de
// Metas. Sem estado, sem Vue — funções determinísticas, fáceis de testar (ver
// specs/useMetasFormat.spec.js). Money em CENTAVOS ponta a ponta (inteiro).
//
// Por que próprio: o único formatador de moeda do codebase (ShopifyOrderItem)
// recebe um float em unidade inteira e usa locale 'en'/'USD'. Aqui o domínio é
// BRL em centavos, locale pt-BR — outro contrato. Mantemos local ao módulo CRM.

// Formatador BRL pt-BR, sem casas decimais (vendas no varejo são inteiros de R$;
// "R$ 3.000"). Instância única reaproveitada (Intl.NumberFormat é caro de criar).
const BRL = new Intl.NumberFormat('pt-BR', {
  style: 'currency',
  currency: 'BRL',
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
});

const BRL_CENTS = new Intl.NumberFormat('pt-BR', {
  style: 'currency',
  currency: 'BRL',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

/**
 * Centavos (inteiro) → "R$ 3.000". Trata null/undefined como 0.
 * Negativo (estorno) preserva o sinal: -150000 → "-R$ 1.500".
 * @param {number|null} centavos
 * @returns {string}
 */
export function formatCentavos(centavos) {
  const c = Number(centavos);
  if (!Number.isFinite(c)) return BRL.format(0);
  return BRL.format(Math.trunc(c) / 100);
}

/**
 * Centavos → "R$ 3.000,50" (com centavos). Para os casos raros em que o valor
 * não é redondo e a precisão importa (ex.: preview de meta diária com resto).
 * @param {number|null} centavos
 * @returns {string}
 */
export function formatCentavosPrecise(centavos) {
  const c = Number(centavos);
  if (!Number.isFinite(c)) return BRL_CENTS.format(0);
  return BRL_CENTS.format(Math.trunc(c) / 100);
}

/**
 * Meta diária resultante = floor(meta_minima_centavos / dias_uteis).
 *
 * O FORM é dono desta fórmula (B5 do plano): o backend não força igualdade com
 * o floor — confia no valor confirmado pelo admin. Floor (não round) porque a
 * meta diária é um piso conservador: a soma dos dias nunca deve estourar a
 * mínima por arredondamento p/ cima.
 *
 * Retorna null quando a entrada é inválida (dias_uteis ausente/<=0 ou mínima
 * ausente/negativa) — sinaliza "preview ainda não disponível" (gate do botão
 * "Confirmar e salvar").
 *
 * @param {number|null} metaMinimaCentavos
 * @param {number|null} diasUteis
 * @returns {number|null} centavos por dia, ou null se não computável
 */
export function metaDiariaCentavos(metaMinimaCentavos, diasUteis) {
  // null/undefined/'' = "ainda não preenchido" → null (não 0). Number(null) é 0,
  // então testamos a ausência ANTES de coagir (senão a mínima vazia viraria 0 e o
  // preview apareceria cedo demais, destravando o botão salvar — B5).
  if (metaMinimaCentavos === null || metaMinimaCentavos === undefined) {
    return null;
  }
  if (diasUteis === null || diasUteis === undefined || diasUteis === '') {
    return null;
  }
  const minima = Number(metaMinimaCentavos);
  const dias = Number(diasUteis);
  if (!Number.isFinite(minima) || minima < 0) return null;
  if (!Number.isInteger(dias) || dias <= 0) return null;
  return Math.floor(minima / dias);
}

/**
 * Reais (string/number digitada pelo admin) → centavos inteiros.
 * Aceita "3.000", "3000", "3000,50", "3.000,50" (pt-BR) e number puro.
 * Retorna null se não for parseável (campo vazio = null, não 0).
 * @param {string|number|null} input
 * @returns {number|null}
 */
export function reaisToCentavos(input) {
  if (input === null || input === undefined || input === '') return null;
  if (typeof input === 'number') {
    return Number.isFinite(input) ? Math.round(input * 100) : null;
  }
  // pt-BR: '.' = milhar, ',' = decimal. Remove milhares, troca decimal por ponto.
  const cleaned = String(input)
    .trim()
    .replace(/\s/g, '')
    .replace(/R\$\s?/i, '')
    .replace(/\./g, '')
    .replace(',', '.');
  if (cleaned === '' || !/^-?\d*\.?\d*$/.test(cleaned)) return null;
  const reais = Number(cleaned);
  if (!Number.isFinite(reais)) return null;
  return Math.round(reais * 100);
}

/**
 * Centavos → string de reais p/ exibir num input (sem símbolo, vírgula decimal
 * só quando há resto). 300000 → "3000"; 300050 → "3000,50".
 * @param {number|null} centavos
 * @returns {string}
 */
export function centavosToReaisInput(centavos) {
  // null/undefined/'' = "sem valor" → input vazio (não "0"). Number(null) é 0,
  // então testamos a ausência antes de coagir (senão o campo nasceria com "0").
  if (centavos === null || centavos === undefined || centavos === '') return '';
  const c = Number(centavos);
  if (!Number.isFinite(c)) return '';
  const reais = Math.trunc(c) / 100;
  return Number.isInteger(reais)
    ? String(reais)
    : reais.toFixed(2).replace('.', ',');
}

/**
 * Período do mês corrente em "YYYY-MM-01" no fuso America/Sao_Paulo.
 * Brasil sem DST desde 2019 → UTC-3 estável; ainda assim ancoramos no NOME do
 * fuso (não no offset) via Intl, conforme B10 do plano.
 * @param {Date} [base=new Date()]
 * @returns {string}
 */
export function currentPeriodo(base = new Date()) {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'America/Sao_Paulo',
    year: 'numeric',
    month: '2-digit',
  }).formatToParts(base);
  const year = parts.find(p => p.type === 'year').value;
  const month = parts.find(p => p.type === 'month').value;
  return `${year}-${month}-01`;
}

/**
 * "2026-06-01" → "junho de 2026" (rótulo humano pt-BR).
 * @param {string} periodo
 * @returns {string}
 */
export function periodoLabel(periodo) {
  if (!periodo) return '';
  const [year, month] = periodo.split('-').map(Number);
  if (!year || !month) return periodo;
  // Dia 15 ao meio-dia evita virada de fuso na formatação.
  const d = new Date(Date.UTC(year, month - 1, 15, 12));
  const label = new Intl.DateTimeFormat('pt-BR', {
    month: 'long',
    year: 'numeric',
    timeZone: 'UTC',
  }).format(d);
  return label;
}

/**
 * Data de hoje em pt-BR longo, fuso America/Sao_Paulo: "quinta-feira, 18 de junho".
 * Usado no `meta` do AppHeader da Home do Operador (redesign F3). Sem ano (a tela é
 * "hoje"); o "de" entre dia e mês é a convenção pt-BR (Intl já o insere com a opção
 * day:'numeric' month:'long'). Fuso pelo NOME (não offset) — consistente com o resto
 * do módulo (B10).
 * @param {Date} [base=new Date()]
 * @returns {string}
 */
export function dataExtensaHoje(base = new Date()) {
  return new Intl.DateTimeFormat('pt-BR', {
    timeZone: 'America/Sao_Paulo',
    weekday: 'long',
    day: 'numeric',
    month: 'long',
  }).format(base);
}

/**
 * Data de hoje em pt-BR curto com prefixo "Hoje", fuso America/Sao_Paulo:
 * "Hoje, 18 jun". Usado no `meta` do AppHeader da Home do Admin (redesign F3).
 * @param {Date} [base=new Date()]
 * @returns {string}
 */
export function dataCurtaHoje(base = new Date()) {
  const dm = new Intl.DateTimeFormat('pt-BR', {
    timeZone: 'America/Sao_Paulo',
    day: 'numeric',
    month: 'short',
  })
    .format(base)
    // Intl pt-BR retorna "18 de jun."; queremos "18 jun" (sem "de" e sem ponto).
    .replace(' de ', ' ')
    .replace('.', '');
  return `Hoje, ${dm}`;
}

// ---------------------------------------------------------------------------
// Datas em BRT (A2 da auditoria 2026-08-03)
// ---------------------------------------------------------------------------
// O app roda em UTC e o negócio é em America/Sao_Paulo. `toLocaleDateString`
// sem `timeZone` segue o relógio da MÁQUINA do usuário — no servidor/CI (UTC) e
// em qualquer cliente fora do BRT o mesmo timestamp rende um dia diferente
// (uma tarefa de 03/09 21:00 BRT vira 04/09 no rótulo). Estes formatadores são
// o lugar único onde o fuso do negócio é ancorado pelo NOME (não pelo offset),
// mesma doutrina do `currentPeriodo` acima (B10).
//
// NÃO usar isto para preencher `<input type="datetime-local">`: aquele input é
// local por contrato do HTML e tem helper próprio (`toDatetimeLocal`).

export const BRT_TIME_ZONE = 'America/Sao_Paulo';

// Intl.DateTimeFormat é caro de instanciar — uma instância por formato, criada
// na carga do módulo e reaproveitada.
const DDMM = new Intl.DateTimeFormat('pt-BR', {
  timeZone: BRT_TIME_ZONE,
  day: '2-digit',
  month: '2-digit',
});

const DDMMAA = new Intl.DateTimeFormat('pt-BR', {
  timeZone: BRT_TIME_ZONE,
  day: '2-digit',
  month: '2-digit',
  year: '2-digit',
});

const DDMMAAAA = new Intl.DateTimeFormat('pt-BR', {
  timeZone: BRT_TIME_ZONE,
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
});

const DIA_MES_CURTO = new Intl.DateTimeFormat('pt-BR', {
  timeZone: BRT_TIME_ZONE,
  day: 'numeric',
  month: 'short',
});

const HORA_MINUTO = new Intl.DateTimeFormat('pt-BR', {
  timeZone: BRT_TIME_ZONE,
  hour: '2-digit',
  minute: '2-digit',
});

// Chave de calendário BRT — "YYYY-MM-DD". en-CA porque é o único locale comum
// que já entrega ISO ordenável, mesmo truque do `currentPeriodo`.
const DATE_KEY = new Intl.DateTimeFormat('en-CA', {
  timeZone: BRT_TIME_ZONE,
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
});

// Coage entrada (ISO string | Date | number) para Date válido, ou null.
function toDate(value) {
  if (value === null || value === undefined || value === '') return null;
  const d = value instanceof Date ? value : new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
}

const fmtWith = (formatter, value) => {
  const d = toDate(value);
  return d ? formatter.format(d) : '';
};

/** ISO → "03/09" (BRT). '' se inválido/ausente. */
export const formatDataDdMm = value => fmtWith(DDMM, value);

/** ISO → "03/09/26" (BRT). '' se inválido/ausente. */
export const formatDataDdMmAa = value => fmtWith(DDMMAA, value);

/** ISO → "03/09/2026" (BRT). '' se inválido/ausente. */
export const formatDataDdMmAaaa = value => fmtWith(DDMMAAAA, value);

/** ISO → "3 de set." (BRT), forma curta de calendário. '' se inválido/ausente. */
export const formatDataDiaMesCurto = value => fmtWith(DIA_MES_CURTO, value);

/** ISO → "14:30" (BRT). '' se inválido/ausente. */
export const formatHoraCurta = value => fmtWith(HORA_MINUTO, value);

/**
 * Dia do calendário BRT como chave comparável ("YYYY-MM-DD"), '' se inválido.
 * Comparar chaves — e não instantes — é o jeito à prova de fuso/DST de responder
 * "é hoje?" / "é amanhã?".
 */
export const brtDateKey = value => fmtWith(DATE_KEY, value);

/**
 * Percentual de progresso (0–100, sem extrapolar) p/ a largura da barra.
 * meta 0 → 0 (evita divisão por zero). Acima da meta satura em 100.
 * @param {number} valorCentavos
 * @param {number} metaCentavos
 * @returns {number} 0..100
 */
export function progressoPercent(valorCentavos, metaCentavos) {
  const valor = Number(valorCentavos) || 0;
  const meta = Number(metaCentavos) || 0;
  if (meta <= 0) return 0;
  const pct = (valor / meta) * 100;
  if (!Number.isFinite(pct)) return 0;
  return Math.max(0, Math.min(100, pct));
}
